import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { makeStyles } from '@material-ui/styles';
import {
  DataGrid,
  GridColDef,
  GridColumns,
  GridRenderCellParams,
  GridRowData,
  GridRowParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import React, { useEffect, useRef, useState } from 'react';

type Row = { [key: string]: any };

interface IListViewProps<DataRowType extends { id: number | string }> {
  data: DataRowType[];
  columns: GridColumns;
  noResultsMessage?: string;
  loading?: boolean;
  disabledSelection?: boolean;
  onChangeSelection?: (selectedItem: DataRowType | undefined) => void;
  onRowDoubleClick?: (params: GridRowData) => void;
  onReorder?: (sourceId: number, targetId: number) => void;
}

export function ReorderableDataGrid<DataRowType extends { id: number | string }>(
  props: IListViewProps<DataRowType>
): JSX.Element {
  const { data, columns, noResultsMessage, loading, disabledSelection, onRowDoubleClick, onReorder } = props;
  const classes = useStyles();
  const [draggedRowId, setDraggedRowId] = useState<number | string | null>(null);

  const gridColumns: GridColDef[] = columns.map((col) => ({
    ...col,
    sortable: col.sortable,
    renderCell: col.renderCell
      ? col.renderCell
      : (params: GridRenderCellParams) => (
          <DraggableRow
            row={params.row as Row}
            field={col.field}
            handleReorder={(sourceId: number, targetId: number) => onReorder && onReorder(sourceId, targetId)}
            onDragEnter={(id) => setDraggedRowId(id)}
            onDrop={() => setDraggedRowId(null)}
          />
        ),
  }));

  const handleOnSelectionModelChange = (selectionModel: GridSelectionModel) => {
    if (props.onChangeSelection) {
      if (selectionModel.length === 1 && data !== null) {
        const selectedRow = data.find((row: DataRowType) => row.id === selectionModel[0]);
        props.onChangeSelection(selectedRow as DataRowType);
      }
    }
  };

  return (
    <DataGrid
      className={classes.dataGrid}
      rows={data}
      loading={loading}
      autoHeight
      rowHeight={48}
      headerHeight={64}
      columns={gridColumns}
      getRowId={(row) => row.id}
      getRowClassName={(params) => {
        // Apply the highlight class based on the drag state
        return params.row.id === draggedRowId ? classes.highlightRow : '';
      }}
      onSelectionModelChange={handleOnSelectionModelChange}
      components={{
        // Collapses resize icon space, since nothing gets rendered
        // eslint-disable-next-line react/display-name
        ColumnResizeIcon: () => <></>,
        // eslint-disable-next-line react/display-name
        NoRowsOverlay: () => <div className={classes.NoRowsOverlay}>{noResultsMessage ?? 'No rows'}</div>,
      }}
      hideFooterPagination
      disableColumnMenu
      hideFooter
      disableSelectionOnClick={disabledSelection}
      onRowDoubleClick={(params: GridRowParams) => onRowDoubleClick && onRowDoubleClick(params.row)}
    />
  );
}

const DraggableRow = ({
  row,
  field,
  handleReorder,
  onDragEnter,
  onDrop,
}: {
  row: Row;
  field: string;
  handleReorder: (sourceId: number, targetId: number) => void;
  onDragEnter: (id: number) => void;
  onDrop: () => void;
}) => {
  const rowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = rowRef.current;
    if (!element) return;

    const cleanUpDraggable = draggable({
      element,
      getInitialData: () => ({ id: row.id }),
    });

    const cleanUpDropTarget = dropTargetForElements({
      element,
      onDrop: ({ source }) => {
        if (source && typeof source.data.id === 'number') {
          if (source.data.id !== row.id) {
            handleReorder(source.data.id, row.id);
          }
        }
        onDrop();
      },
      onDragEnter: () => {
        onDragEnter(row.id);
      },
    });

    // Attach a global event listener for drag end
    const handleDragEnd = () => {
      onDrop(); // Reset dragged state
    };

    document.addEventListener('dragend', handleDragEnd);

    // Clean up on unmount or change
    return () => {
      cleanUpDraggable();
      cleanUpDropTarget();
    };
  }, [row.id, handleReorder]);

  return (
    <div ref={rowRef} style={{ width: '100%', cursor: 'grab', display: 'flex' }}>
      {row[field]}
    </div>
  );
};

const useStyles = makeStyles(() => ({
  dataGrid: {
    backgroundColor: '#ffffff',
    border: 'none',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 700,
      fontSize: 14,
      color: '#000000',
    },
    '& .MuiDataGrid-columnHeaders': {
      border: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-cell': {
      fontWeight: 400,
      fontSize: 14,
      color: '#000000',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'none',
    },
    '& .MuiDataGrid-row:hover': {
      backgroundColor: 'rgba(255, 221, 0, 0.5)',
    },
    '& .MuiDataGrid-row.Mui-selected': {
      backgroundColor: 'rgba(255, 221, 0, 0.5)',
    },
    '& .MuiDataGrid-row.Mui-selected:hover': {
      backgroundColor: 'rgba(255, 221, 0, 0.5)',
    },
    '& .MuiDataGrid-footerContainer': {
      minHeight: 0,
      height: 32,
    },
  },
  NoRowsOverlay: {
    inset: '64px 0 0 0',
    display: 'flex',
    position: 'absolute',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(248, 248, 248, 0.38)',
  },
  highlightRow: {
    borderTop: '4px solid rgba(255, 221, 0, 1)',
  },
  row: {
    display: 'flex',
    width: '100%',
  },
}));

export default ReorderableDataGrid;
